/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import PostType from '@linc-technologies/linced-common/enums/post-type';
import type Tag from '@linc-technologies/linced-common/models/posts/tag';
import type Group from 'linced-app/models/group';
import type Person from 'linced-app/models/person';
import type Page from 'linced-app/models/posts/page';
import type School from 'linced-app/models/school';

// Proto: notifications.Subscription.
export default class Subscription extends Model {
  declare [Type]: 'notifications/subscription';

  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the last time the resource was modified in seconds from
  // the epoch.
  @attr('number')
  declare updateTime: number | undefined;

  // schoolId is the unique identifier of the school to filter the
  // subscription type by.
  // Required: Yes.
  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  // campusId is the unique identifier of the campus to filter the
  // subscription type by.
  // Required: No.
  @attr('string', { defaultValue: '' })
  declare campusId: string;

  // subscriberId is the unique identifier of the person who subscribed to this
  // content.
  @belongsTo('person', { async: true, inverse: null })
  declare subscriberId: AsyncBelongsTo<Person>;

  @alias('subscriberId')
  declare subscriber: AsyncBelongsTo<Person>;

  //// Subscription parameters - at least one must be specified.
  // postType determines the type of post being subscribed to.
  @attr('number', { defaultValue: 0 })
  declare postType: EnumValue<typeof PostType> | 0;

  @computed('postType')
  get postTypeLabel() {
    return PostType.getLabel(this.postType);
  }

  // pageId is the unique identifier of the Page being subscribed to.
  @belongsTo('posts/page', { async: true, inverse: null })
  declare pageId: AsyncBelongsTo<Page>;

  @alias('pageId')
  declare page: AsyncBelongsTo<Page>;

  // groupId is the unique identifier of the Group being subscribed to.
  @belongsTo('group', { async: true, inverse: null })
  declare groupId: AsyncBelongsTo<Group>;

  @alias('groupId')
  declare group: AsyncBelongsTo<Group>;

  // personId is the unique identifier of the Person being subscribed to.
  @belongsTo('person', { async: true, inverse: null })
  declare personId: AsyncBelongsTo<Person>;

  @alias('personId')
  declare person: AsyncBelongsTo<Person>;

  // tagId is the unique identifier of the Tag being subscribed to.
  @belongsTo('posts/tag', { async: true, inverse: null })
  declare tagId: AsyncBelongsTo<Tag>;

  @alias('tagId')
  declare tag: AsyncBelongsTo<Tag>;
}
